// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// Decode a file (or a recorded AudioBuffer) into a mono, 48 kHz Float32 signal
// ready for RNNoise. Uses the browser's own decoder (handles mp3/m4a/aac/ogg/
// flac/wav and the audio track of many video containers). AudioContext work must
// happen on the main thread — workers can't construct one.

import { downmixToMono, resampleLinear } from './audio';
import { RNNOISE_RATE as RATE, type PreparedAudio } from './types';

let sharedCtx: AudioContext | OfflineAudioContext | null = null;

function decodeContext(): BaseAudioContext {
  if (!sharedCtx) {
    const Ctor: typeof AudioContext =
      (window.AudioContext as typeof AudioContext) ||
      ((window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    // A 48 kHz context nudges Safari's decodeAudioData toward 48 kHz output.
    try {
      sharedCtx = new Ctor({ sampleRate: RATE });
    } catch {
      sharedCtx = new Ctor();
    }
  }
  return sharedCtx as BaseAudioContext;
}

/** Downmix + resample an AudioBuffer to mono 48 kHz. */
async function bufferToMono48k(buffer: AudioBuffer): Promise<Float32Array> {
  const channels: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    channels.push(buffer.getChannelData(c).slice());
  }
  const mono = downmixToMono(channels);
  if (buffer.sampleRate === RATE) return mono;
  return resampleTo48k(mono, buffer.sampleRate);
}

/** High-quality resample via OfflineAudioContext, with a linear fallback. */
async function resampleTo48k(mono: Float32Array, inRate: number): Promise<Float32Array> {
  if (inRate === RATE || mono.length === 0) return mono;
  try {
    const outLen = Math.max(1, Math.ceil((mono.length * RATE) / inRate));
    const OAC: typeof OfflineAudioContext =
      (window.OfflineAudioContext as typeof OfflineAudioContext) ||
      ((window as unknown as { webkitOfflineAudioContext: typeof OfflineAudioContext })
        .webkitOfflineAudioContext);
    const oac = new OAC(1, outLen, RATE);
    const buf = oac.createBuffer(1, mono.length, inRate);
    buf.copyToChannel(mono as Float32Array<ArrayBuffer>, 0);
    const src = oac.createBufferSource();
    src.buffer = buf;
    src.connect(oac.destination);
    src.start();
    const rendered = await oac.startRendering();
    return rendered.getChannelData(0).slice();
  } catch {
    return resampleLinear(mono, inRate, RATE);
  }
}

/** Decode an uploaded file into a prepared mono 48 kHz signal. */
export async function prepareFromFile(file: File): Promise<PreparedAudio> {
  const arrayBuffer = await file.arrayBuffer();
  const ctx = decodeContext();
  const audioBuffer = await decodeAudioDataCompat(ctx, arrayBuffer);
  const samples = await bufferToMono48k(audioBuffer);
  return {
    samples,
    sampleRate: RATE,
    sourceName: file.name || 'recording',
    duration: samples.length / RATE,
  };
}

/** Wrap a recorded (mono, arbitrary-rate) Float32 buffer as prepared audio. */
export async function prepareFromRecording(
  mono: Float32Array,
  inRate: number,
  name: string,
): Promise<PreparedAudio> {
  const samples = inRate === RATE ? mono : await resampleTo48k(mono, inRate);
  return { samples, sampleRate: RATE, sourceName: name, duration: samples.length / RATE };
}

/** Safari's older decodeAudioData is callback-only; support both shapes. */
function decodeAudioDataCompat(
  ctx: BaseAudioContext,
  data: ArrayBuffer,
): Promise<AudioBuffer> {
  return new Promise((resolve, reject) => {
    const p = ctx.decodeAudioData(
      data,
      (buf) => resolve(buf),
      (err) => reject(err ?? new Error('Could not decode this audio')),
    );
    if (p && typeof (p as Promise<AudioBuffer>).then === 'function') {
      (p as Promise<AudioBuffer>).then(resolve, reject);
    }
  });
}

/** Best-effort close of the shared context (called on teardown). */
export function releaseDecodeContext(): void {
  if (sharedCtx && 'close' in sharedCtx && typeof sharedCtx.close === 'function') {
    try {
      (sharedCtx as AudioContext).close();
    } catch {
      /* ignore */
    }
  }
  sharedCtx = null;
}
